/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Wraps a MedplumClient so every FHIR write stamps the canonical Unify
 * `meta.profile` (server-derived, via `withUnifyProfile`). This is the single
 * chokepoint that makes `Project.strictMode` validate profile-expressible rules
 * on ALL app writes, without touching individual call sites.
 *
 * `withUnifyProfile` is a no-op for unprofiled resource types and idempotent, so
 * wrapping is safe for every write (including infra resources). Residual
 * (non-profile-expressible) rules are still enforced via the `$unify-validate-write`
 * operation where used; this wrapper handles the broad profile-validation layer.
 */
import type { MedplumClient } from '@medplum/core';
import type { Bundle, Resource } from '@medplum/fhirtypes';

import { withUnifyProfile } from '@unify/fhir-model/profiles';

/** Write methods whose first argument is the resource to stamp. */
const RESOURCE_FIRST_WRITES = [
  'createResource',
  'updateResource',
  'createResourceIfNoneExist',
  'upsertResource',
] as const;

function stampBundle(bundle: Bundle): Bundle {
  if (!bundle.entry) return bundle;
  return {
    ...bundle,
    entry: bundle.entry.map((e) => (e.resource ? { ...e, resource: withUnifyProfile(e.resource) } : e)),
  };
}

/**
 * Monkey-patch the client's write methods in place to stamp `meta.profile`.
 * Returns the same client for convenience.
 */
export function stampUnifyWrites(medplum: MedplumClient): MedplumClient {
  const client = medplum as unknown as Record<string, any>;

  for (const method of RESOURCE_FIRST_WRITES) {
    const original = client[method];
    if (typeof original !== 'function') continue;
    client[method] = (resource: Resource, ...rest: unknown[]) =>
      original.call(medplum, withUnifyProfile(resource), ...rest);
  }

  const originalBatch = client.executeBatch;
  if (typeof originalBatch === 'function') {
    client.executeBatch = (bundle: Bundle, ...rest: unknown[]) =>
      originalBatch.call(medplum, stampBundle(bundle), ...rest);
  }

  return medplum;
}
